/**
 * 微信公众号登录相关接口
 * 对应 Apipost: 用户端 > 用户 > 微信登录
 */

/** 后端接口基础地址 */
function getBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
}

// ==================== 类型定义 ====================

/** 接口1 - 获取授权跳转地址 的响应 */
export interface WxAuthUrlResponse {
  code: number;
  message: string;
  data: {
    authorize_url: string; // 微信OAuth2.0授权跳转地址
    state: string; // 用于CSRF校验的state参数
  };
}

/** 接口2 - 微信授权回调 的响应 */
export interface WxCallbackResponse {
  code: number;
  message: string;
  data: {
    token: string; // 用户认证 Token
    token_type: string; // "Bearer"
    expires_in: number; // Token有效期（秒），如 604800
    is_new_user: boolean | string; // 是否为新注册用户
    user: WxUserInfo; // 平台用户信息
    wechat_user: WxWechatUser; // 微信用户信息
  };
}

/** 平台用户信息 */
export interface WxUserInfo {
  uid: number; // 用户ID
  account: string; // 用户账号
  nickname: string; // 用户昵称
  avatar: string; // 用户头像URL
  phone: string; // 手机号（脱敏）
  real_name: string;
  status: number; // 1-正常，0-禁用
  is_apead: number;
  now_money: string;
  add_time: string; // 注册时间
}

/** 微信用户信息 */
export interface WxWechatUser {
  openid: string;
  unionid: string;
  nickname: string;
  avatar: string;
  sex: number; // 0-未知，1-男，2-女
  country: string;
  province: string;
  city: string;
}

// ==================== 接口调用 ====================

/**
 * 接口1：获取微信授权跳转地址
 * GET /home/login/wechat/auth-url?redirect_uri=xxx
 *
 * @param redirectUri - 授权后回调地址（需URL编码）
 */
export async function fetchWxAuthUrl(
  redirectUri: string,
): Promise<WxAuthUrlResponse["data"]> {
  const base = getBaseUrl();
  const url = `${base}/home/login/wechat/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`;

  const res = await fetch(url, { method: "GET" });

  if (!res.ok) {
    throw new Error(`获取授权地址失败: ${res.status}`);
  }

  const json: WxAuthUrlResponse = await res.json();

  if (json.code !== 200) {
    throw new Error(json.message || "获取授权地址失败");
  }

  return json.data;
}

/**
 * 接口2：微信授权登录回调
 * GET /home/login/wechat/callback?code=xxx&state=xxx
 *
 * @param code - 微信授权码（微信回调自动携带）
 * @param state - 自定义state参数（CSRF校验）
 */
export async function fetchWxCallback(
  code: string,
  state: string,
): Promise<WxCallbackResponse["data"]> {
  const base = getBaseUrl();
  const url = `${base}/home/login/wechat/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;

  const res = await fetch(url, { method: "GET" });

  if (!res.ok) {
    throw new Error(`授权回调失败: ${res.status}`);
  }

  const json: WxCallbackResponse = await res.json();
  if (json.code !== 200) {
    throw new Error(json.message || "微信授权失败");
  }

  return json.data;
}
